import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../lib/utils';
import {
  Upload,
  Search,
  MoreVertical,
  Trash2,
  Download,
  Eye,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileSpreadsheet,
  File,
  FolderOpen,
  FolderPlus,
  Folder,
  Filter,
  Grid,
  List,
  Plus,
  Edit,
  Users,
  ArrowLeft,
  X,
  Mail,
  UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iconos según tipo de archivo
const getFileIcon = (type) => {
  if (type?.includes('image')) return FileImage;
  if (type?.includes('video')) return FileVideo;
  if (type?.includes('audio')) return FileAudio;
  if (type?.includes('pdf')) return FileText;
  if (type?.includes('spreadsheet') || type?.includes('excel') || type?.includes('xlsx')) return FileSpreadsheet;
  return File;
};

// Colores según tipo de archivo
const getFileColor = (type) => {
  if (type?.includes('image')) return 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300';
  if (type?.includes('video')) return 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300';
  if (type?.includes('audio')) return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300';
  if (type?.includes('pdf')) return 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300';
  if (type?.includes('spreadsheet') || type?.includes('excel')) return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300';
  return 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300';
};

// Colores para etiquetas de línea de negocio
const businessLineColors = {
  allianz: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  qualitas: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  insignia: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  flipping: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  general: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
};

// Formato de tamaño de archivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Roles disponibles para compartir
const SHARE_ROLES = [
  { id: 'agente', label: 'Agentes' },
  { id: 'asistente', label: 'Asistentes' },
  { id: 'developer', label: 'Desarrolladores' },
  { id: 'asesor', label: 'Asesores' },
  { id: 'viewer', label: 'Viewers' }
];

export const DocumentsPage = () => {
  const { user, canDelete, isAdmin } = useAuth();
  const { selectedLine, isAllLines, changeBusinessLine, filterByBusinessLine, availableLines } = useBusinessLine();
  const fileInputRef = useRef(null);

  // Estado de usuarios (para compartir)
  const [allUsers, setAllUsers] = useState([]);

  // Estado de carpetas
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderFormData, setFolderFormData] = useState({
    name: '',
    type: 'shared',
    business_line: 'general',
    shared_with_emails: [],
    shared_with_roles: []
  });
  const [newEmail, setNewEmail] = useState('');

  // Documentos ahora tienen folder_id obligatorio
  const [documents, setDocuments] = useState([
    {
      id: '1',
      name: 'Póliza Allianz Vida - Juan Pérez.pdf',
      type: 'application/pdf',
      size: 1250000,
      business_line: 'allianz',
      folder_id: 'f1',
      uploaded_at: '2026-01-20T14:30:00',
      uploaded_by: 'Admin EstraPat',
      owner_id: 'a1',
      url: '#'
    },
    {
      id: '2',
      name: 'Contrato Base Allianz.pdf',
      type: 'application/pdf',
      size: 520000,
      business_line: 'allianz',
      folder_id: 'f1',
      uploaded_at: '2026-01-18T10:15:00',
      uploaded_by: 'Admin EstraPat',
      owner_id: 'a1',
      url: '#'
    },
  ]);
  const [search, setSearch] = useState('');
  const [lineFilter, setLineFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    business_line: 'general',
    files: []
  });
  const [uploading, setUploading] = useState(false);

  const loadFolders = async () => {
    try {
      setFoldersLoading(true);
      const res = await axios.get(`${API}/documents/folders`);
      // Normalize backend fields to frontend names
      const normalized = (res.data || []).map(f => ({
        ...f,
        shared_with_roles: f.shared_roles || [],
        shared_with_emails: f.shared_emails || [],
        business_line: f.linea_negocio || 'general',
        type: 'shared',
      }));
      setFolders(normalized);
    } catch (error) {
      console.error('Error cargando carpetas:', error);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Cargar carpetas y usuarios al inicio
  useEffect(() => {
    loadFolders();
    if (isAdmin || ['developer', 'master'].includes(user?.role)) {
      axios.get(`${API}/users`)
        .then(r => setAllUsers(r.data || []))
        .catch(() => {});
    }
  }, [isAdmin, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funciones para manejar emails de compartir
  const addShareEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!email.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }
    if (folderFormData.shared_with_emails.includes(email)) {
      toast.error('Este email ya fue agregado');
      return;
    }
    setFolderFormData(prev => ({
      ...prev,
      shared_with_emails: [...prev.shared_with_emails, email]
    }));
    setNewEmail('');
  };

  const removeShareEmail = (email) => {
    setFolderFormData(prev => ({
      ...prev,
      shared_with_emails: prev.shared_with_emails.filter(e => e !== email)
    }));
  };

  const toggleShareRole = (roleId) => {
    setFolderFormData(prev => ({
      ...prev,
      shared_with_roles: prev.shared_with_roles.includes(roleId)
        ? prev.shared_with_roles.filter(r => r !== roleId)
        : [...prev.shared_with_roles, roleId]
    }));
  };

  // Funciones para carpetas
  const handleCreateFolder = async () => {
    if (!folderFormData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    try {
      if (editingFolder) {
        await axios.put(`${API}/documents/folders/${editingFolder.id}`, {
          name: folderFormData.name,
          shared_roles: folderFormData.shared_with_roles,
          shared_emails: folderFormData.shared_with_emails,
          linea_negocio: folderFormData.business_line === 'general' ? null : folderFormData.business_line,
        });
        toast.success('Carpeta actualizada');
      } else {
        await axios.post(`${API}/documents/folders`, {
          name: folderFormData.name,
          shared_roles: folderFormData.shared_with_roles,
          shared_emails: folderFormData.shared_with_emails,
          linea_negocio: folderFormData.business_line === 'general' ? null : folderFormData.business_line,
        });
        toast.success('Carpeta creada');
      }
      await loadFolders();
      setFolderDialogOpen(false);
      resetFolderForm();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al guardar la carpeta');
    }
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderFormData({
      name: folder.name,
      type: 'shared',
      business_line: folder.business_line || 'general',
      shared_with_emails: folder.shared_with_emails || [],
      shared_with_roles: folder.shared_with_roles || []
    });
    setFolderDialogOpen(true);
  };

  const handleDeleteFolder = async (folderId) => {
    // Verificar si tiene documentos locales
    const docsInFolder = documents.filter(d => d.folder_id === folderId);
    if (docsInFolder.length > 0) {
      toast.error(`Esta carpeta tiene ${docsInFolder.length} documento(s). Elimínalos primero.`);
      return;
    }
    if (!window.confirm('¿Estás seguro de eliminar esta carpeta?')) return;
    try {
      await axios.delete(`${API}/documents/folders/${folderId}`);
      await loadFolders();
      if (currentFolder?.id === folderId) setCurrentFolder(null);
      toast.success('Carpeta eliminada');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al eliminar la carpeta');
    }
  };

  const resetFolderForm = () => {
    setEditingFolder(null);
    setFolderFormData({
      name: '',
      type: 'shared',
      business_line: 'general',
      shared_with_emails: [],
      shared_with_roles: []
    });
    setNewEmail('');
  };

  // El backend ya filtra carpetas por rol; todas las retornadas son accesibles.
  // Esta función es para compatibilidad con lógica local adicional.
  const canAccessFolder = () => true;

  // Descargar documento
  const handleDownload = (doc) => {
    const link = document.createElement('a');
    link.href = doc.url || '#';
    link.download = doc.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Descarga iniciada');
  };

  // Ver documento
  const handleView = (doc) => {
    window.open(doc.url || '#', '_blank');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setUploadData(prev => ({
        ...prev,
        files: [...prev.files, ...files]
      }));
    }
  };

  const handleUpload = async () => {
    if (!currentFolder) {
      toast.error('Debes seleccionar una carpeta primero');
      return;
    }
    if (uploadData.files.length === 0) {
      toast.error('Selecciona al menos un archivo');
      return;
    }

    setUploading(true);
    try {
      const newDocs = uploadData.files.map((file, index) => ({
        id: Date.now().toString() + index,
        name: file.name,
        type: file.type,
        size: file.size,
        business_line: currentFolder.business_line,
        folder_id: currentFolder.id,
        uploaded_at: new Date().toISOString(),
        uploaded_by: user?.full_name || 'Usuario',
        owner_id: user?.id,
        url: URL.createObjectURL(file)
      }));

      setDocuments(prev => [...newDocs, ...prev]);
      toast.success(`${uploadData.files.length} archivo(s) subido(s) a "${currentFolder.name}"`);
      setDialogOpen(false);
      setUploadData({ business_line: 'general', files: [] });
    } catch (error) {
      toast.error('Error al subir archivos');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    setDocuments(prev => prev.filter(doc => doc.id !== id));
    toast.success('Documento eliminado');
  };

  const removeFileFromUpload = (index) => {
    setUploadData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Filtrar carpetas accesibles
  const accessibleFolders = folders.filter(canAccessFolder);

  const sharedFolders = accessibleFolders.filter(f =>
    f.type === 'shared' &&
    (isAllLines || !f.business_line || f.business_line === 'general' || f.business_line === selectedLine)
  );

  // Documentos de la carpeta actual
  const currentFolderDocs = currentFolder 
    ? documents.filter(d => d.folder_id === currentFolder.id)
    : [];

  // Filtrar por búsqueda
  const filteredDocs = currentFolderDocs.filter(doc =>
    doc.name.toLowerCase().includes(search.toLowerCase())
  );

  // Calcular estadísticas
  const totalSize = filteredDocs.reduce((sum, doc) => sum + doc.size, 0);
  const docsByType = {
    pdf: filteredDocs.filter(d => d.type?.includes('pdf')).length,
    image: filteredDocs.filter(d => d.type?.includes('image')).length,
    spreadsheet: filteredDocs.filter(d => d.type?.includes('excel') || d.type?.includes('spreadsheet')).length,
    other: filteredDocs.filter(d => !d.type?.includes('pdf') && !d.type?.includes('image') && !d.type?.includes('excel')).length
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documentos</h1>
          <p className="text-muted-foreground">Gestiona y organiza los documentos por línea de negocio</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedLine} onValueChange={changeBusinessLine}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las aseguradoras</SelectItem>
              <SelectItem value="allianz">Allianz</SelectItem>
              <SelectItem value="insignia">Insignia Life</SelectItem>
              <SelectItem value="qualitas">Quálitas</SelectItem>
              <SelectItem value="flipping">Bienes Raíces</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <>
            {/* Botón Crear Carpeta */}
            <Dialog open={folderDialogOpen} onOpenChange={(open) => {
              setFolderDialogOpen(open);
              if (!open) resetFolderForm();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-folder-btn">
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Crear Carpeta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingFolder ? 'Editar Carpeta' : 'Nueva Carpeta'}</DialogTitle>
                  <DialogDescription>
                    {editingFolder ? 'Modifica los datos de la carpeta' : 'Crea una nueva carpeta para organizar documentos'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="space-y-2">
                    <Label>Nombre de la Carpeta *</Label>
                    <Input
                      value={folderFormData.name}
                      onChange={(e) => setFolderFormData({ ...folderFormData, name: e.target.value })}
                      placeholder="Nombre de la carpeta"
                      data-testid="folder-name-input"
                    />
                  </div>
                  
                  {/* Opciones de compartir - Solo para carpetas compartidas y admin */}
                  {folderFormData.type === 'shared' && isAdmin && (
                    <div className="space-y-4 pt-2 border-t">
                      <Label className="text-base font-semibold">¿Con quién compartir?</Label>
                      
                      {/* Compartir por rol */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Por tipo de usuario:</Label>
                        <div className="flex flex-wrap gap-2">
                          {SHARE_ROLES.map(role => (
                            <Button
                              key={role.id}
                              type="button"
                              variant={folderFormData.shared_with_roles.includes(role.id) ? "default" : "outline"}
                              size="sm"
                              onClick={() => toggleShareRole(role.id)}
                              className={folderFormData.shared_with_roles.includes(role.id) ? "bg-[#2d8659]" : ""}
                            >
                              <UserCheck className="h-3 w-3 mr-1" />
                              {role.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Compartir por email */}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Por correo electrónico:</Label>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="correo@ejemplo.com"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addShareEmail())}
                          />
                          <Button type="button" variant="outline" onClick={addShareEmail}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {folderFormData.shared_with_emails.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {folderFormData.shared_with_emails.map(email => (
                              <Badge key={email} variant="secondary" className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {email}
                                <button
                                  type="button"
                                  onClick={() => removeShareEmail(email)}
                                  className="ml-1 hover:text-red-500"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Línea de Negocio</Label>
                    <Select
                      value={folderFormData.business_line}
                      onValueChange={(v) => setFolderFormData({ ...folderFormData, business_line: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Línea" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        {availableLines.filter(l => l.id !== 'all').map(line => (
                          <SelectItem key={line.id} value={line.id}>{line.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setFolderDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                      onClick={handleCreateFolder}
                      disabled={!folderFormData.name.trim()}
                    >
                      {editingFolder ? 'Guardar' : 'Crear Carpeta'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Botón Importar Documentos - Solo visible si hay una carpeta seleccionada */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#1a365d] hover:bg-[#1a365d]/90" 
                  data-testid="import-docs-btn"
                  disabled={!currentFolder}
                  title={!currentFolder ? "Selecciona una carpeta primero" : "Subir archivos"}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Subir Archivo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Subir Archivos</DialogTitle>
                  <DialogDescription>
                    {currentFolder 
                      ? `Los archivos se guardarán en: "${currentFolder.name}"`
                      : 'Selecciona una carpeta primero'
                    }
                  </DialogDescription>
                </DialogHeader>
                {currentFolder ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-muted rounded-lg flex items-center gap-2">
                      <Folder className="h-5 w-5 text-[#1a365d]" />
                      <div>
                        <p className="font-medium">{currentFolder.name}</p>
                        <p className="text-xs text-muted-foreground">Carpeta compartida</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Archivos</Label>
                      <div
                        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Haz clic para seleccionar archivos
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, XLSX, Word, Imágenes, Videos, Audio, etc.
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                        accept="*/*"
                      />
                    </div>

                    {uploadData.files.length > 0 && (
                      <div className="space-y-2">
                        <Label>Archivos seleccionados ({uploadData.files.length})</Label>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {uploadData.files.map((file, index) => {
                            const FileIcon = getFileIcon(file.type);
                            return (
                              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileIcon className="h-4 w-4 flex-shrink-0" />
                                  <span className="text-sm truncate">{file.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({formatFileSize(file.size)})
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 flex-shrink-0"
                                  onClick={() => removeFileFromUpload(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => {
                        setDialogOpen(false);
                        setUploadData({ business_line: 'general', files: [] });
                      }}>
                        Cancelar
                      </Button>
                      <Button 
                        className="bg-[#2d8659] hover:bg-[#2d8659]/90"
                        onClick={handleUpload}
                        disabled={uploading || uploadData.files.length === 0}
                      >
                        {uploading ? 'Subiendo...' : `Subir ${uploadData.files.length} archivo(s)`}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Folder className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Debes seleccionar una carpeta antes de subir archivos</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
          )}
        </div>
      </div>

      {/* Vista de Carpetas - Solo se muestra cuando NO hay carpeta seleccionada */}
      {!currentFolder && (
        <div className="space-y-6">
          {/* Carpetas Compartidas */}
          {foldersLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d]" />
            </div>
          ) : sharedFolders.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#1a365d]" />
                  Carpetas Compartidas
                </CardTitle>
                <CardDescription>Acceso para múltiples usuarios según configuración</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {sharedFolders.map(folder => (
                    <div
                      key={folder.id}
                      className="group relative p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors hover:border-[#1a365d]"
                      onClick={() => setCurrentFolder(folder)}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="relative">
                          <Folder className="h-12 w-12 text-[#1a365d] mb-2" />
                          <Users className="absolute -bottom-1 -right-1 h-4 w-4 text-[#2d8659] bg-white rounded-full" />
                        </div>
                        <span className="text-sm font-medium truncate w-full">{folder.name}</span>
                        <Badge className={`${businessLineColors[folder.business_line] || businessLineColors.general} text-[10px] mt-1`}>
                          {BUSINESS_LINES.find(l => l.id === folder.business_line)?.name || 'General'}
                        </Badge>
                        {/* Mostrar con quién está compartida */}
                        <div className="flex flex-wrap gap-1 justify-center mt-2">
                          {folder.shared_with_roles?.map(role => (
                            <Badge key={role} variant="outline" className="text-[9px]">
                              {SHARE_ROLES.find(r => r.id === role)?.label || role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {/* Acciones de carpeta - Solo admin */}
                      {isAdmin && (
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-6 w-6">
                                <MoreVertical className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditFolder(folder); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mensaje informativo */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="py-6 text-center">
              <FolderPlus className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <h3 className="font-medium mb-1">Selecciona una carpeta para ver o subir archivos</h3>
              <p className="text-sm text-muted-foreground">
                Los archivos deben estar organizados en carpetas. Haz clic en una carpeta para ver su contenido.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navegación y contenido cuando estamos dentro de una carpeta */}
      {currentFolder && (
        <>
          {/* Breadcrumb de navegación */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setCurrentFolder(null)}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver
                  </Button>
                  <span className="text-muted-foreground">/</span>
                  <Folder className="h-4 w-4 text-[#1a365d]" />
                  <span className="font-medium">{currentFolder.name}</span>
                  <Badge variant="outline" className="ml-2">
                    <Users className="h-3 w-3 mr-1" />
                    Compartida
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{filteredDocs.length} archivo(s)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredDocs.length}</div>
                <p className="text-xs text-muted-foreground">{formatFileSize(totalSize)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">PDFs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{docsByType.pdf}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Imágenes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{docsByType.image}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Excel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{docsByType.spreadsheet}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Otros</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{docsByType.other}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar documentos..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="search-docs"
                  />
                </div>
                <div className="flex gap-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents Grid/List */}
          {filteredDocs.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-1">Esta carpeta está vacía</h3>
                <p className="text-muted-foreground text-sm">
                  {search ? 'No se encontraron documentos con ese criterio' : 'Sube archivos para comenzar a llenar esta carpeta'}
                </p>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDocs.map((doc) => {
            const FileIcon = getFileIcon(doc.type);
            const fileColor = getFileColor(doc.type);
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`doc-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-3 rounded-lg ${fileColor}`}>
                      <FileIcon className="h-6 w-6" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleView(doc)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(doc)}>
                          <Download className="mr-2 h-4 w-4" />
                          Descargar
                        </DropdownMenuItem>
                        {canDelete && (
                          <DropdownMenuItem
                            onClick={() => handleDelete(doc.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Eliminar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="font-medium text-sm truncate mb-1" title={doc.name}>
                    {doc.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={businessLineColors[doc.business_line] || businessLineColors.general}>
                      {BUSINESS_LINES.find(l => l.id === doc.business_line)?.name || 'General'}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <p>{formatFileSize(doc.size)}</p>
                    <p>{formatDate(doc.uploaded_at)}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredDocs.map((doc) => {
                const FileIcon = getFileIcon(doc.type);
                const fileColor = getFileColor(doc.type);
                return (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`doc-list-${doc.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`p-2 rounded-lg ${fileColor}`}>
                        <FileIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-sm truncate">{doc.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${businessLineColors[doc.business_line] || businessLineColors.general} text-xs`}>
                            {BUSINESS_LINES.find(l => l.id === doc.business_line)?.name || 'General'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{formatDate(doc.uploaded_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Download className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
};
